import type { FormedWord, Premium, PremiumKind, ScoredWord, ScoringTile } from '../types';
import { cellsCovered } from './board';
import { RACK_CAPACITY, rackSpan } from './tiles';

/** What each premium multiplies a word's total by. */
const WORD_MULTIPLIER: Record<PremiumKind, number> = { '2W': 2, '3W': 3 };

/** Awarded on top of a move's own score when it lays a full rack: {@link RACK_CAPACITY} characters. */
export const BINGO_BONUS = 50;

/** The bingo bonus a move earns: {@link BINGO_BONUS} when the faces it lays come to a full rack, otherwise 0. */
export const bingoFor = (placedFaces: readonly string[]): number =>
  rackSpan(placedFaces) === RACK_CAPACITY ? BINGO_BONUS : 0;

/**
 * What each word of a move scores, in the order given. A word sums the points of every tile it runs through (a tile
 * shared by two crossing words counts in both), with premiums applied only where a tile in `placedIds` sits on them:
 * a premium covered on an earlier turn is spent. Each premium multiplies the word's total, and premiums in one word
 * multiply together.
 *
 * `tiles` is every tile on the board, the move's included, each with the points it scores.
 */
export function scoreWords(
  words: readonly Pick<FormedWord, 'word' | 'cells'>[],
  premiums: readonly Premium[],
  tiles: readonly ScoringTile[],
  placedIds: ReadonlySet<string | number>,
): ScoredWord[] {
  const premiumAt = new Map<string, PremiumKind>(premiums.map((p) => [`${p.row},${p.col}`, p.kind]));
  const tileAt = new Map<string, ScoringTile>();
  for (const tile of tiles) {
    for (const cell of cellsCovered(tile)) tileAt.set(`${cell.row},${cell.col}`, tile);
  }

  return words.map(({ word, cells }) => {
    let points = 0;
    let multiplier = 1;
    const counted = new Set<string | number>();

    for (const cell of cells) {
      const tile = tileAt.get(`${cell.row},${cell.col}`);
      if (!tile || counted.has(tile.id)) continue;
      counted.add(tile.id);

      // A cluster tile may sit on a premium with any of its cells in the word, not only the first one read.
      const under = placedIds.has(tile.id)
        ? cells.filter((c) => tileAt.get(`${c.row},${c.col}`) === tile).map((c) => premiumAt.get(`${c.row},${c.col}`))
        : [];
      for (const premium of under) {
        if (premium) multiplier *= WORD_MULTIPLIER[premium];
      }
      points += tile.points;
    }

    return { word, points: points * multiplier };
  });
}

/** What a move's words come to together: the sum of {@link scoreWords}. */
export const scoreMove = (...args: Parameters<typeof scoreWords>): number =>
  scoreWords(...args).reduce((total, word) => total + word.points, 0);
